import React, { useState, useEffect, useCallback } from 'react';
import { PracticeScreen, PreKPracticeSettings, FactAttemptTracker } from '../types';
import IDoScreen from './IDoScreen';
import WeDoScreen from './WeDoScreen';
import YouDoScreen from './YouDoScreen';
import AccuracyScreen from './AccuracyScreen';
import { factIdToValue, PREK_PRACTICE_TRACKS } from '../shared';

interface WhatComesNextPracticeProps {
  settings: PreKPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
  initialPracticeType?: 'learning' | 'accuracy';
}

const WhatComesNextPractice: React.FC<WhatComesNextPracticeProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
  initialPracticeType = 'learning'
}) => {
  // Determine initial screen based on practice type
  const getInitialScreen = (): PracticeScreen => {
    switch (initialPracticeType) {
      case 'accuracy':
        return 'accuracy';
      default:
        return 'ido';
    }
  };

  const [currentScreen, setCurrentScreen] = useState<PracticeScreen>(getInitialScreen());
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [failedFacts, setFailedFacts] = useState<string[]>([]);
  const [weDoFailedFacts, setWeDoFailedFacts] = useState<string[]>([]);
  const [isRetryingFailedFacts, setIsRetryingFailedFacts] = useState(false);
  const [weDoCompletedFacts, setWeDoCompletedFacts] = useState<string[]>([]);

  // Convert fact ID to target number using shared constants
  const factToTarget = useCallback((factId: string): number => {
    return factIdToValue(PREK_PRACTICE_TRACKS.WHATCOMESNEXT, factId);
  }, []);

  // Generate all 7 What Comes Next facts (FACT1595 to FACT1601, targets 3-9)
  const getAllTrackFacts = useCallback((): { factId: string; digit: number }[] => {
    const allFacts = [];
    for (let target = 3; target <= 9; target++) {
      allFacts.push({
        factId: `FACT${1592 + target}`,
        digit: target
      });
    }
    return allFacts;
  }, []);





  // Get current facts to work with based on screen and failures
  const getCurrentFacts = useCallback((): string[] => {
    if (currentScreen === 'wedo' && isRetryingFailedFacts && weDoFailedFacts.length > 0) {
      // Only repeat the failed facts, not the whole sequence
      return weDoFailedFacts;
    }
    if (failedFacts.length > 0 && (currentScreen === 'wedo' || currentScreen === 'youdo')) {
      return failedFacts;
    }
    return factsToPractice;
  }, [failedFacts, currentScreen, factsToPractice, weDoFailedFacts, isRetryingFailedFacts]);

  // Handle I Do screen completion
  const handleIDoComplete = useCallback(() => {
    setCurrentScreen('wedo');
    setCurrentFactIndex(0);
  }, []);

  // Handle We Do screen completion for current fact
  const handleWeDoFactComplete = useCallback(() => {
    const currentFacts = getCurrentFacts();
    if (currentFactIndex < currentFacts.length - 1) {
      setCurrentFactIndex(currentFactIndex + 1);
    } else {
      // Completed current sequence
      if (!isRetryingFailedFacts && weDoFailedFacts.length > 0) {
        // First time through - now retry only the failed facts
        setIsRetryingFailedFacts(true);
        setCurrentFactIndex(0);
      } else {
        // Either no failed facts, or finished retrying failed facts - move to You Do
        setCurrentScreen('youdo');
        setCurrentFactIndex(0);
        setIsRetryingFailedFacts(false);
        setWeDoFailedFacts([]);
      }
    }
  }, [currentFactIndex, getCurrentFacts, weDoFailedFacts, isRetryingFailedFacts]);

  // Handle We Do wrong answer - add fact to failed queue
  const handleWeDoWrongAnswer = useCallback((factId: string) => {
    setWeDoFailedFacts(prev => {
      if (!prev.includes(factId)) {
        return [...prev, factId];
      }
      return prev;
    });
  }, []);

  // Handle We Do correct answer - mark fact as completed
  const handleWeDoCorrectAnswer = useCallback((factId: string) => {
    setWeDoCompletedFacts(prev => {
      if (!prev.includes(factId)) {
        return [...prev, factId];
      }
      return prev;
    });
    // Remove from failed facts if it was there
    setWeDoFailedFacts(prev => prev.filter(id => id !== factId));
  }, []);

  // Handle You Do screen completion for current fact
  const handleYouDoFactComplete = useCallback(() => {
    if (currentFactIndex < factsToPractice.length - 1) {
      setCurrentFactIndex(currentFactIndex + 1);
    } else {
      // All facts completed in You Do, move to Accuracy practice
      setCurrentScreen('accuracy');
    }
  }, [currentFactIndex, factsToPractice.length]);



  // Get current fact ID
  const getCurrentFactId = useCallback((): string => {
    const facts = getCurrentFacts();
    return facts[currentFactIndex] || facts[0];
  }, [getCurrentFacts, currentFactIndex]);

  // Get current target number
  const getCurrentTarget = useCallback((): number => {
    return factToTarget(getCurrentFactId());
  }, [getCurrentFactId, factToTarget]);


  const renderCurrentScreen = () => {
    const currentFactId = getCurrentFactId();
    const currentTarget = getCurrentTarget();

    switch (currentScreen) {
      case 'ido':
        return (
          <IDoScreen
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handleIDoComplete}
            onBack={onBack}
          />
        );

      case 'wedo': {
        // Convert facts to required format (using target numbers instead of digits)
        const factsWithTargets = factsToPractice.map(factId => ({
          factId,
          digit: factToTarget(factId) // Using digit field but with target numbers
        }));
        return (
          <WeDoScreen
            settings={settings}
            factsToPractice={factsWithTargets}
            onComplete={() => {
              setCurrentScreen('youdo');
              setCurrentFactIndex(0);
            }}
            onBack={onBack}
          />
        );
      }

      case 'youdo': {
        // Convert facts to required format (using target numbers instead of digits)
        const youDoFactsWithTargets = factsToPractice.map(factId => ({
          factId,
          digit: factToTarget(factId) // Using digit field but with target numbers
        }));
        return (
          <YouDoScreen
            settings={settings}
            factsToPractice={youDoFactsWithTargets}
            onComplete={() => {
              setCurrentScreen('accuracy');
            }}
            onBack={onBack}
          />
        );
      }

      case 'accuracy': {
        // Use ALL track facts for accuracy practice (not just the learning facts)
        return (
          <AccuracyScreen
            settings={settings}
            factsToPractice={getAllTrackFacts()}
            onComplete={() => {
              // Page reload handles routing based on updated statuses
              window.location.reload();
            }}
            onBack={onBack}
          />
        );
      }

      default:
        return null;
    }
  };

  // Guard against empty factsToPractice array
  if (factsToPractice.length === 0) {
    console.error('WhatComesNextPractice: factsToPractice array is empty after practice started');
    onBack();
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      {renderCurrentScreen()}
    </div>
  );
};

export default WhatComesNextPractice;